import React, { lazy, Suspense, useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Heading from 'components/shared/heading';

// Import Icons
import FileAddIcon from 'components/icons/FileAdd';
import KeySquareIcon from 'components/icons/KeySquare';
import DashboardIcon from 'components/icons/Dashboard';
import Intercom2Icon from 'components/icons/Intercom2';
import ListCheckIcon from 'components/icons/ListCheck';
import SizeFullscreenIcon from 'components/icons/SizeFullscreen';
import StarShineIcon from 'components/icons/StarShine';
import ActualSizeIcon from 'components/icons/ActualSize';

import styles from './styles.module.scss';
import useVideo from '../../../hooks/use-video';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.215, 0.61, 0.355, 1] },
  },
};

const getIcon = (icon) => {
  switch (icon) {
    case 'file-add':
      return <FileAddIcon />;
    case 'key-square':
      return <KeySquareIcon />;
    case 'dashboard':
      return <DashboardIcon />;
    case 'intercom':
      return <Intercom2Icon />;
    case 'list-check':
      return <ListCheckIcon />;
    case 'size-fullscreen':
      return <SizeFullscreenIcon />;
    case 'star-shine':
      return <StarShineIcon />;
    case 'actual-size':
      return <ActualSizeIcon />;
    default:
      return null;
  }
};

const MediaWithGrid = ({
  title = null,
  titleHighlighted = null,
  titleTag = 'h2',
  description = null,
  image = null,
  videoId = null,
  items = [],
  theme = '',
}) => {
  const [sectionRef, inView] = useInView({ threshold: 0.2 });
  const controls = useAnimation();
  const { isVideoMuted } = useVideo();
  const playerRef = useRef(null);
  const mediaContainerRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  const appendImageToCard = useCallback((cardId) => {
    const imageDiv = document.getElementById(`mwg-img-${cardId}`);
    if (imageDiv && mediaContainerRef.current) {
      mediaContainerRef.current.appendChild(imageDiv);
    }
  }, []);

  // Handle image appending for this specific card
  useEffect(() => {
    if (image?.id && !videoId) {
      appendImageToCard(image.id);
    }
  }, [image, videoId, appendImageToCard]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  // Handle Wistia playing when in view
  useEffect(() => {
    if (inView && videoId) {
      playerRef?.current?.play?.();
    } else {
      playerRef?.current?.pause?.();
    }
    return () => {
      playerRef?.current?.pause?.();
    };
  }, [inView, videoId]);

  const placeStyleToWistia = useCallback((wistiaElementParam) => {
    const wistiaElement = wistiaElementParam; // || playerRef.current;
    // This runs after the Wistia player component has been rendered
    // setTimeout(() => {
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
                  /* This CSS is now safely inside the Shadow DOM */
                video {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                    position: absolute !important;
                    top: 0;
                    left: 0;
                }
                div {
                    position: initial !important;
                }
                .click-for-sound-btn {
                    display: none !important;
                }
              `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleWistiaReady = useCallback(
    (event) => {
      const player = event?.target;
      if (!player) return;
      placeStyleToWistia(player);
    },
    [placeStyleToWistia]
  );

  return (
    <section
      ref={sectionRef}
      className={cn(styles.mediaWithGrid, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      <div className="container">
        <motion.div initial="hidden" animate={controls} variants={containerVariants}>
          {/* Header */}
          {(title || description) && (
            <div className={styles.header}>
              {title && (
                <motion.div variants={itemVariants}>
                  <Heading
                    className={styles.title}
                    tag={titleTag}
                    size="xxl"
                    highlightedWordsWithoutWrap={false}
                  >
                    {titleHighlighted && (
                      <span className={styles.highlight}>{titleHighlighted}&nbsp;</span>
                    )}
                    {title}
                  </Heading>
                </motion.div>
              )}
              {description && (
                <motion.div variants={itemVariants}>
                  <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </motion.div>
              )}
            </div>
          )}

          {/* Media */}
          {(image || videoId) && (
            <motion.div className={styles.mediaContainer} variants={itemVariants}>
              {isClient && videoId && (
                <Suspense fallback={null}>
                  <WistiaPlayerLazy
                    ref={playerRef}
                    mediaId={videoId}
                    autoPlay
                    playlistLoop
                    muted={isVideoMuted}
                    className={styles.wistiaPlayer}
                    onApiReady={handleWistiaReady}
                    poster={false}
                    swatch={false}
                    style={{ width: '100%', height: '100%', maxHeight: '100%' }}
                    resumable
                  />
                </Suspense>
              )}
              {image && !videoId && (
                <div className={styles.imageContainer} ref={mediaContainerRef} />
              )}
            </motion.div>
          )}

          {/* Grid */}
          {items && items.length > 0 && (
            <motion.div className={styles.grid} variants={containerVariants}>
              {items.map((item, index) => {
                const Icon = getIcon(item.icon);
                return (
                  <motion.div key={index} className={styles.card} variants={itemVariants}>
                    {Icon && <div className={styles.iconWrapper}>{Icon}</div>}
                    <div
                      className={styles.cardDescription}
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

MediaWithGrid.propTypes = {
  titleHighlighted: PropTypes.string,
  title: PropTypes.string,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  description: PropTypes.string,
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  image: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
    alt: PropTypes.string,
  }),
  videoId: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      description: PropTypes.string,
    })
  ),
};

export default MediaWithGrid;
